import React, { useState } from "react";
import { ScreenerTable } from "./ScreenerTable";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import { greyText } from "../../styles";
import { PageButton } from "../../components/visuals/PageButton";
import { SearchBar } from "../../components/visuals/SearchBar";
import {
  BackgroundContainer,
  MainColumn as Column,
  ElementContainer,
  FlexibleContainer,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { useScreener } from "../../hooks/useScreener";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { MESSAGES } from "../../messages";
import {
  CustomDropdown,
  onSelectionFuncType,
} from "../../components/visuals/PageFilter/PageFilter";
import resetIcon from "../../assets/svg/reset.svg";
import metricsIcon from "../../assets/svg/metrics_icon_hovered.svg";
import { BASE_CONFIG } from "./constants";
import { MetricsModal } from "./MetricsModal";

export const Screener = (): JSX.Element => {
  const {
    isLoading,
    companyMetrics,
    companyMetricsConfigData,
    fundOptions,
    sectorOptions,
    allCompanyOptions,
    metricsOptions,
    parameters,
    screenerTableKey,
    dynamicMetricColumns,
    dispatchFundUpdate,
    dispatchFilterUpdate,
    dispatchMetricValueFiltersUpdate,
    dispatchPaginationUpdate,
    dispatchSortUpdate,
    dispatchResetFilters,
  } = useScreener();

  const [show, setShow] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const selectedFund = parameters.filters.portfolio_index_id;

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address={"screening-tool"} />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              Screening Tool
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={20} color={greyText}>
              {MESSAGES.SCREENING_TOOL.DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
          <RowContainer ai={"center"} jc="space-between" mt={1} ml={8} mb={8}>
            <ElementContainer ai="center">
              <CustomDropdown
                key={selectedFund || "defaultFundKey"}
                options={fundOptions}
                isLoading={isLoading}
                onSelectOption={dispatchFundUpdate}
                {...(selectedFund
                  ? {
                      selectedFund,
                      override: {
                        key: selectedFund,
                        displayValue: selectedFund,
                      },
                    }
                  : {
                      placeholder: { key: null, displayValue: "Select fund" },
                    })}
              />
              <PageButton
                key={
                  allCompanyOptions[0]?.key || "defaultCompaniesPageButtonKey"
                }
                isLoading={!metricsOptions.length}
                content={
                  <SearchBar
                    key={allCompanyOptions[0]?.key || "defaultCompaniesKey"}
                    options={allCompanyOptions}
                    placeholder="Search company"
                    isMultiSelect={true}
                    onSelectOptions={
                      dispatchFilterUpdate as onSelectionFuncType
                    }
                  />
                }
              />
              <PageButton
                content={
                  <CompassParagraph cursor="pointer">
                    {`Search metrics (${
                      parameters.filters.metrics.length - 1
                    })`}
                  </CompassParagraph>
                }
                action={(): void => setShowModal(true)}
                image={metricsIcon}
                isLoading={isLoading}
                key="resetFilters"
                he={true}
              />
              <CustomDropdown
                key={`${sectorOptions[0]?.key}-${screenerTableKey}-defaultSectorKey`}
                options={sectorOptions}
                placeholder={{
                  key: null,
                  displayValue: "Select sector",
                }}
                isMultiSelect={true}
                onSelectOptions={dispatchFilterUpdate as onSelectionFuncType}
                isLoading={!sectorOptions.length}
              />
            </ElementContainer>
            <FlexibleContainer mr={"38px"}>
              <PageButton
                content={
                  <CompassParagraph cursor="pointer">
                    Reset filters
                  </CompassParagraph>
                }
                action={dispatchResetFilters}
                image={resetIcon}
                isLoading={isLoading}
                key="resetFilters"
                he={true}
              />
            </FlexibleContainer>
          </RowContainer>
        </NavBarContainer>
        <RowContainer ml={20} mt={1}>
          <ScreenerTable
            tableKey={screenerTableKey}
            data={companyMetrics}
            isLoading={isLoading}
            config={companyMetricsConfigData || BASE_CONFIG}
            parameters={parameters}
            handlePagination={dispatchPaginationUpdate}
            handleSort={dispatchSortUpdate}
            handleMetricFilterUpdate={dispatchMetricValueFiltersUpdate}
            dynamicColumns={dynamicMetricColumns}
          />
        </RowContainer>
      </Column>
      <MetricsModal
        show={showModal}
        handleClose={(): void => setShowModal(false)}
        handleSelectMetrics={dispatchFilterUpdate as onSelectionFuncType}
        compassMetrics={metricsOptions}
      />
    </BackgroundContainer>
  );
};
